import Avatar from "../../assets/icons/Avatar";
import {formatDateNoTime, getFriendlyDate, shortFormatDate} from "../../utils/friendlyDate";
import Loader from "../../components/loader/Loader";
import {detectTextDirection} from "../../utils/detectTextDirection";
import {Button} from "antd";
import {Link} from "react-router-dom";
import NoSelectedConversationView from "./NoSelectedConversationView";
import React, {useEffect, useRef, useState} from "react";
import {ConversationRow, Segment} from "../../types/ConversationProps";
import {sendConversationsMessage} from "../../api/sendConversationsMessage";
import {ToastSkin} from "../../context/ToastContext";
import {getFullName} from "../../utils/getFullName";
import useAppContext from "../../hooks/useAppContext";
import useUserContext from "../../hooks/useUserContext";
import useToast from "../../hooks/useToast";
import useFullscreen from "../../hooks/useFullscreen";
import {CONVERSATION_VIEWS} from "./Conversations";
import {TeamIcon} from "../../assets/icons/Team";
import ConversationMessageBubble from "./ConversationMessageBubble";
import SegmentMessageBubble from "./SegmentMessageBubble";
import {setConversationsAsSeen} from "../../api/setConversationsAsSeen";
import LongArrowLeft from "../../assets/icons/LongArrowLeft";
import {formatPhoneNumber} from "react-phone-number-input";
import {sendWidgetConversationsMessage} from "../../api/sendWidgetConversationsMessage";
import {SetConversationAsResolved} from "../../api/setConversationAsResolved";
import {BurgerIcon} from "../../assets/icons/BurgerIcon";
import {BurgerDots} from "../../assets/icons/BurgerDots";

interface ConversationRoomProps {
    currentConversation: ConversationRow | null;
    currentSegment: Segment | null;
    view: CONVERSATION_VIEWS;
    unSelectConversation: () => void;
    openClientPanel?: (clientId: number) => void;
}

const ConversationRoom = ({ currentConversation, currentSegment, view, unSelectConversation, openClientPanel }: ConversationRoomProps) => {
    const { data: { conversationMessages, isChatSocketReady }, actions: { loadConversationMessages, setConversationAsSeen, addMessageToConversation } } = useAppContext();
    const { selectedDomain, onboardingStatus: { isFullyOnboarded } } = useUserContext();
    const [nextMessage, setNextMessage] = useState('');
    const { toggleFullScreen, FullScreenIcon } = useFullscreen("conversations-container");
    const isSegmentView = view === CONVERSATION_VIEWS.SEGMENT;
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
    const clientName = currentConversation ? getFullName(currentConversation.firstName, currentConversation.lastName) || currentConversation.guestName : '';
    const isWidgetConversation = !!currentConversation?.widgetSessionId;
    const isWidgetConversationOpen = isWidgetConversation && !currentConversation?.resolvedAt;
    const segmentName = currentSegment ? currentSegment.name : '';
    const currentMessages = currentConversation?.id ? conversationMessages.get(currentConversation.id) : null;
    const currentSegmentMessages = currentSegment?.id ? currentSegment.messages : null;
    const [isLoadingCurrentMessages, setIsLoadingCurrentMessages] = useState(false);
    const [isSendingMessage, setIsSettingMessage] = useState(false);
    const isCurrentConversationActive = !currentConversation?.widgetSessionId && currentConversation?.activationWindowDeadline ? new Date(currentConversation?.activationWindowDeadline) > new Date() : false;
    const { showToast, closeToast } = useToast();
    const containerRef = useRef<HTMLDivElement>(null);
    const [initialScrollDone, setInitialScrollDone] = useState(false);
    const [shownMessagesCount, setShownMessagesCount] = useState(30);
    const messagesToShow = currentMessages ? [...currentMessages.slice(0, shownMessagesCount)].reverse() : [];
    const prevScrollTopRef = useRef(0);
    const prevScrollHeightRef = useRef(0);

    const onTextAreaChange = (newVal: string) => {
        setNextMessage(newVal);
        autoExpandTextArea();
    }

    const onSendMessageClick = async () => {
        if(selectedDomain && (currentConversation?.clientId || currentConversation?.widgetSessionId)) {
            const nextMessageContent = `${nextMessage}`;
            setNextMessage('');
            setIsSettingMessage(true);
            if(currentConversation.widgetSessionId) {
                const response = await sendWidgetConversationsMessage(selectedDomain, currentConversation.id, nextMessageContent);
                const newMessage = response?.data;
                if (newMessage) {
                    addMessageToConversation(newMessage);
                }
            } else if(currentConversation.clientId) {
                const response = await sendConversationsMessage(selectedDomain, currentConversation.clientId, currentConversation.id, nextMessageContent);
                const newMessage = response?.data?.message;
                if (newMessage) {
                    addMessageToConversation(newMessage);
                }
            }
            setIsSettingMessage(false);
        }
    }

    const onDemoMessageButtonClick = () => {
        showToast({ skin: ToastSkin.neutral, text: 'This button is for simulation purposes only. in the actual message, your client can click on it.' });
    }

    const autoExpandTextArea = () => {
        if(!textAreaRef.current) return;
        const style = getComputedStyle(textAreaRef.current); // Get the computed styles
        const lineHeight = parseFloat(style.lineHeight); // Get the line height
        const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom); // Calculate padding

        textAreaRef.current.style.height = '1px'; // Reset height to shrink if necessary
        const contentHeight = textAreaRef.current.scrollHeight - padding; // Adjust height by excluding padding
        textAreaRef.current.style.height = (contentHeight)+"px";

        textAreaRef.current.rows = Math.ceil(contentHeight / lineHeight);
    };

    const getConversationMessages = async () => {
        if(isSegmentView && currentSegment) {
        } else if(currentConversation?.id && !conversationMessages.get(currentConversation.id)) {
            setIsLoadingCurrentMessages(true);
            await loadConversationMessages(currentConversation.id);
            setIsLoadingCurrentMessages(false);
        }
    }

    const toDateString = (ts: string | number | Date) => {
        const d = new Date(ts);
        return d.toISOString().split("T")[0]; // e.g. "2025-08-22"
    };

    const isDifferentDay = (a: string | number | Date, b: string | number | Date) => {
        return toDateString(a) !== toDateString(b);
    };

    useEffect(() => {
        setInitialScrollDone(false);
        autoExpandTextArea();
        getConversationMessages();
        if(selectedDomain && currentConversation?.id) {
            setConversationAsSeen(currentConversation.id);
            setConversationsAsSeen(selectedDomain, currentConversation.id);
        }
    }, [currentConversation?.id]);

    useEffect(() => {
        const container = containerRef.current;
        if(!container) return;
        const isStickingToBottom = container.scrollTop + container.offsetHeight > container.scrollHeight - 2000;
        if ((isStickingToBottom || !initialScrollDone) && (currentMessages?.length || 0) > 0) {
            requestAnimationFrame(() => {
                container.scrollTop = container.scrollHeight;
                setInitialScrollDone(true);
            });
        }
    }, [currentMessages, currentMessages?.length, initialScrollDone]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleScroll = () => {
            if (container.scrollTop < 300) {
                prevScrollHeightRef.current = container.scrollHeight;
                prevScrollTopRef.current = container.scrollTop;
                setShownMessagesCount(shownMessagesCount + 30);
            }
        };

        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [containerRef.current]);

    useEffect(() => {
        const container = containerRef.current;
        if(container && prevScrollHeightRef.current && prevScrollTopRef.current) {
            requestAnimationFrame(() => {
                const prevScrollTop = prevScrollTopRef.current;
                const prevScrollHeight = prevScrollHeightRef.current;
                const newScrollHeight = container.scrollHeight;
                // how much taller the content got
                const diff = newScrollHeight - prevScrollHeight;

                // restore previous visual position
                container.scrollTop = prevScrollTop + diff;
            });
        }
    }, [shownMessagesCount]);
    const title = isSegmentView ? segmentName : clientName;
    const subTitle = currentConversation?.phone ? formatPhoneNumber(`+${currentConversation.phone}`) : '';
    const emailSubscribedMembers = currentSegment?.members.map(member => member.defaultEmailAddress?.marketingStatus === 'SUBSCRIBED')?.length || 0;
    const smsSubscribedMembers = currentSegment?.members.map(member => member.defaultPhoneNumber?.marketingStatus === 'SUBSCRIBED')?.length || 0;
    const label = isSegmentView ? `${emailSubscribedMembers} email subscribers, ${smsSubscribedMembers} sms subscribers` : isCurrentConversationActive ? `The conversation will remain active until ${shortFormatDate(new Date(currentConversation?.activationWindowDeadline!))}` : null;
    const isConversationSelected = isSegmentView ? !!currentSegment : !!currentConversation;
    const defaultImage = isSegmentView ? <div className="image chat-image"><TeamIcon width={37} height={45} color="#fff" /></div>
        : currentConversation?.firstName && currentConversation?.lastName ?
            <div className="initial-letters">{currentConversation?.firstName.substring(0,1).toUpperCase()}{currentConversation?.lastName.substring(0,1).toUpperCase()}</div>
            : currentConversation?.firstName ?
                <div className="initial-letters">{currentConversation?.firstName.substring(0,1).toUpperCase()}</div>
                : <Avatar width={40} height={40} color="#fff"/>;
    const conversationImage = isSegmentView ? defaultImage : currentConversation?.image ? <img src={currentConversation.image} alt={clientName} /> : defaultImage;

    const setAsResolved = () => {
        showToast({ skin: ToastSkin.neutral, text: "Are you sure this conversation is resolved? This will lock the conversation for both you and the customer.", buttons: [
            { text: 'Yes', onClick: async () => {
                    if(selectedDomain && currentConversation?.widgetSessionId) {
                        closeToast();
                        const response = await SetConversationAsResolved(selectedDomain, currentConversation.widgetSessionId);
                        if(response?.data === 'ok') {
                            currentConversation.resolvedAt = new Date();
                            showToast({ skin: ToastSkin.approval, text: 'Conversation was resolved'});
                        } else {
                            showToast({ skin: ToastSkin.error, text: 'Error while trying to resolve conversation. Please try again or contact support'});
                        }
                    }
                }
            },
                { text: 'No, Cancel', onClick: closeToast }]})
    }

    return (
        <div className={`chat-container ${isConversationSelected ? 'selected' : ''}`}>
            {isConversationSelected ? <>
                <div className="chat-header">
                    <div className="left">
                        <div className="back-button" onClick={unSelectConversation}><LongArrowLeft width={24} color="#000" /></div>
                        <div className={`chat-image ${isWidgetConversation ? 'widget' : isCurrentConversationActive ? 'active' : ''}`}>
                            {conversationImage}
                        </div>
                        <div className="chat-title-container">
                            <div className="title" onClick={() => openClientPanel && currentConversation?.clientId ? openClientPanel(currentConversation.clientId) : null}>{title} {currentConversation?.widgetSessionId ? <span>(Support Widget)</span> : null}</div>
                            <div className="subtitle">{subTitle}</div>
                            {isWidgetConversation ? <div className="active-label">{currentConversation?.resolvedAt ? `Resolved ${getFriendlyDate(new Date(currentConversation.resolvedAt))}` : <div>Unresolved. <span onClick={setAsResolved}>Set as resolved</span></div>}</div> : <div className="active-label">{label}</div>}
                        </div>
                    </div>
                    <div className="right">
                        {currentConversation?.clientId && openClientPanel ? <div className="chat-room-action client-info" onClick={() => openClientPanel(currentConversation.clientId)}><BurgerDots width={18} height={18} color='#1575fb' /></div> : null}
                        <div className="chat-room-action full-screen-button-container" onClick={toggleFullScreen}><FullScreenIcon /></div>
                    </div>
                </div>
                <div className="messages-bubble-container" ref={containerRef}>
                    {isLoadingCurrentMessages ? <div className="messages-loader-container"><Loader size={144} /></div> : null}
                    {!isSegmentView && !isLoadingCurrentMessages && currentMessages?.length ? messagesToShow.map((message, index) => {
                        const shouldShowDate = index - 1 < 0 || isDifferentDay(messagesToShow[index - 1].createdAt, message.createdAt ? new Date(message.createdAt) : new Date());
                        return <React.Fragment key={`message-${currentConversation?.id}-${message.externalId}`}>
                            {shouldShowDate ? <div className="conversation-date-separator-container"><div>{formatDateNoTime(message.createdAt ? new Date(message.createdAt) : new Date())}</div></div> : null}
                            <ConversationMessageBubble message={message} onDemoMessageButtonClick={onDemoMessageButtonClick}/>
                        </React.Fragment>
                    }) : null}
                    {isSegmentView && currentSegmentMessages?.length && currentSegment ? [...currentSegmentMessages].reverse().map(message => {
                        return <SegmentMessageBubble key={`message-${currentSegment.id}-${message.id}`} message={message} onDemoMessageButtonClick={onDemoMessageButtonClick}/>;
                    }) : null}
                </div>
                {isCurrentConversationActive || isWidgetConversationOpen ?
                    <div className="message-sender">
                                <textarea className={detectTextDirection(nextMessage)} disabled={!isCurrentConversationActive && !isWidgetConversationOpen} ref={textAreaRef} value={nextMessage}
                                          onChange={(event) => onTextAreaChange(event.target.value)} rows={1}/>
                        <Button size="middle" type="primary" onClick={onSendMessageClick}
                                disabled={(!isCurrentConversationActive && !isWidgetConversationOpen) || !nextMessage || !isChatSocketReady || !isFullyOnboarded}>Send</Button>
                    </div>
                    : isWidgetConversation ? <div className="message-sender disabled">
                        Conversation was resolved {getFriendlyDate(new Date(currentConversation.resolvedAt!))}
                    </div> :
                    <div className="message-sender disabled">
                        Chat is inactive. To activate it, your client must respond to one of your template messages. <Link to="/docs">Learn More</Link>
                    </div>
                }
            </> : <NoSelectedConversationView FullScreenIcon={FullScreenIcon} toggleFullScreen={toggleFullScreen} />}
        </div>
    );
}

export default ConversationRoom;